import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAgpMutations, useAgpQuery } from "../hooks/useAgpApi";
import { AttainmentBar, EditableCell, ElapsedNote, PaceChip, SourceTag, formatTarget } from "../components/AgpUi";
import ActualsPanel from "../components/ActualsPanel";
import { MeetingRatesCard } from "../components/MeetingRates";
import MatrixScroller from "../components/MatrixScroller";
import { Avatar, Card, ErrorState, Pill, Skeleton, TeamDot } from "../../components/Ui";
import { avatarSrc } from "../../hooks/useBdApi";
import { formatDate, formatDateTime, teamColor } from "../../metrics";

export default function AgpTeamPage({ agpYear }) {
  const { teamKey } = useParams();
  const params = useMemo(() => ({ year: agpYear.year }), [agpYear.year]);
  const { data, loading, error, refetch } = useAgpQuery(`teams/${teamKey}`, params);
  const mutations = useAgpMutations();
  const [addingId, setAddingId] = useState("");
  const [showHistory, setShowHistory] = useState(false);

  if (error) return <ErrorState message={error} onRetry={refetch} />;
  if (loading && !data) return <Skeleton height={400} />;

  const { team, teamGoals, members, goals, addableMembers } = data;
  const yearWindow = data.window;
  const canEditTeam = team.canEdit;
  const year = agpYear.year;
  const color = teamColor(team.name);

  const saveTeamDriver = (key, patch) => mutations.putTeam(team.key, year, { drivers: [{ key, ...patch }] });
  const saveMemberDriver = (sfUserId, key, patch) => mutations.putMember(sfUserId, year, team.key, { drivers: [{ key, ...patch }] });
  const saveObjective = (coreObjective) => mutations.putTeam(team.key, year, { coreObjective });

  const applyRollup = async (driver) => {
    if (driver.memberRollup === null || driver.memberRollup === undefined) return;
    await saveTeamDriver(driver.key, { target: driver.memberRollup });
  };

  const headline = ["clientMeetings", "taps", "dealsClosed", "curateAttendees", "signedClients", "concurrentClients", "grossRevenue"]
    .map((k) => teamGoals.drivers.find((d) => d.key === k))
    .filter((d) => d && d.target !== null && d.target !== undefined);

  return (
    <div className="bd-page bd-agp" style={{ "--team-color": color }}>
      <div className="bd-page__head">
        <div>
          <div className="bd-eyebrow">
            <Link to={`/landmark/bd/agp${agpYear.search}`} className="bd-link">
              ← All practices
            </Link>
          </div>
          <h1 className="bd-page__title-with-dot">
            <TeamDot team={team.name} /> {team.practice} · AGP {year}
          </h1>
          <p className="bd-page__lede">
            {team.name}
            {teamGoals.filledOutBy && <> · filled out by {teamGoals.filledOutBy}</>}
            {teamGoals.filledOutDate && <> on {formatDate(teamGoals.filledOutDate)}</>}
            {" · "}
            <ElapsedNote window={yearWindow} />
          </p>
        </div>
        <div className="bd-page__head-meta">
          <Pill tone={teamGoals.status === "final" ? "success" : "warning"}>{teamGoals.status}</Pill>
          {canEditTeam && (
            <button
              type="button"
              className="bd-btn bd-btn--secondary"
              onClick={() => mutations.putTeam(team.key, year, { status: teamGoals.status === "final" ? "draft" : "final" })}
            >
              Mark {teamGoals.status === "final" ? "draft" : "final"}
            </button>
          )}
          <Link to={`/landmark/bd/team/${team.key}`} className="bd-btn bd-btn--ghost">
            Performance view →
          </Link>
        </div>
      </div>

      <Card title="Core objective" actions={canEditTeam ? <span className="bd-muted">click to edit</span> : null}>
        <EditableCell
          value={teamGoals.coreObjective}
          format="text"
          canEdit={canEditTeam}
          align="left"
          multiline
          placeholder={canEditTeam ? "Describe the practice's core objective for the year…" : "No core objective set"}
          onSave={saveObjective}
        />
      </Card>

      {headline.length > 0 && (
        <div className="bd-agp__headline">
          {headline.map((d) => (
            <div key={d.key} className="bd-agp__headline-item">
              <div className="bd-agp__headline-label">
                {d.label} <PaceChip row={d} compact />
              </div>
              <div className="bd-agp__headline-value">
                <strong>{d.actual === null ? "—" : formatTarget(d.actual, d.format)}</strong>
                <span className="bd-muted"> / {formatTarget(d.target, d.format)}</span>
              </div>
              <AttainmentBar row={d} />
            </div>
          ))}
        </div>
      )}

      {goals.map((g) => {
        const rows = teamGoals.drivers.filter((d) => d.goal === g.number);
        if (!rows.length) return null;
        return (
          <Card
            key={g.number}
            title={`Goal ${g.number}: ${g.title}`}
            subtitle={g.description}
            flush
            actions={
              g.number === 1 && canEditTeam && (
                <span className="bd-muted">Revenue actuals are recorded below under "Record actuals"</span>
              )
            }
          >
            <MatrixScroller
              hint={`${members.length} members · scroll sideways to see them all${
                members.some((m) => m.shared) ? " · member actuals count only activity attributed to this practice (click a name for their totals)" : ""
              }`}
            >
              <table className="bd-table bd-agp-table bd-agp-matrix">
                <thead>
                  <tr>
                    <th className="bd-agp-matrix__driver">Key driver</th>
                    {members.map((m) => (
                      <th key={m.sfUserId} className="bd-agp-matrix__member">
                        <Link
                          to={`/landmark/bd/agp/member/${m.sfUserId}${agpYear.search}`}
                          className="bd-agp-matrix__member-link"
                          title={`${m.member.name} · actual on top, full-year goal underneath${
                            m.shared ? ` · only activity attributed to ${team.practice} counts here; click for their totals` : ""
                          }`}
                        >
                          <Avatar name={m.member.name} team={m.member.team} size="sm" src={avatarSrc(m.member)} />
                          <span>
                            {m.member.name}
                            {m.shared && (
                              <small className="bd-muted" title={`Home team: ${m.member.team}`}>
                                {" "}
                                · shared
                              </small>
                            )}
                          </span>
                        </Link>
                      </th>
                    ))}
                    <th className="bd-agp-matrix__total">Members total</th>
                    <th className="bd-agp-matrix__target">Team target</th>
                    <th className="bd-agp-matrix__actual">Actual · % of goal</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((d) => {
                    const isText = d.format === "text";
                    const rollup = d.memberRollup;
                    const differs = !isText && rollup !== null && rollup !== undefined && d.target !== null && d.target !== undefined && Math.round(rollup) !== Math.round(d.target);
                    return (
                      <tr key={d.key}>
                        <td className="bd-agp-matrix__driver">
                          <span className="bd-agp-driver">
                            <span>{d.label}</span>
                            <SourceTag source={d.source} driverKey={d.key} label={d.label} />
                          </span>
                          {d.note && <small className="bd-muted bd-agp-note">{d.note}</small>}
                        </td>
                        {members.map((m) => {
                          const md = m.drivers.find((x) => x.key === d.key) || {};
                          const hasActual = !isText && md.actual !== null && md.actual !== undefined;
                          const hasTarget = !isText && md.target !== null && md.target !== undefined;
                          if (isText) {
                            return (
                              <td key={m.sfUserId} className="bd-agp-matrix__member">
                                <EditableCell value={md.targetText} format="text" canEdit={m.canEdit} onSave={(v) => saveMemberDriver(m.sfUserId, d.key, { targetText: v })} />
                              </td>
                            );
                          }
                          // nothing to show and nothing to edit: a single dash instead of "— / goal —"
                          if (!hasActual && !hasTarget && !m.canEdit) {
                            return (
                              <td key={m.sfUserId} className="bd-agp-matrix__member">
                                <span className="bd-muted">—</span>
                              </td>
                            );
                          }
                          // actual (bold) on top with the pace chip; goal underneath, muted but still editable
                          return (
                            <td key={m.sfUserId} className="bd-agp-matrix__member">
                              <div className="bd-agp-matrix__actual-line">
                                <strong className={hasActual ? "" : "bd-muted"}>{hasActual ? formatTarget(md.actual, d.format) : "—"}</strong>
                                {hasActual && hasTarget && <PaceChip row={md} compact />}
                              </div>
                              <div className="bd-agp-matrix__target-line">
                                <span>goal</span>
                                <EditableCell
                                  value={md.target}
                                  format={d.format}
                                  canEdit={m.canEdit}
                                  muted
                                  title="Full-year goal"
                                  onSave={(v) => saveMemberDriver(m.sfUserId, d.key, { target: v })}
                                />
                              </div>
                              {md.note && <small className="bd-muted bd-agp-note">{md.note}</small>}
                            </td>
                          );
                        })}
                        <td className="bd-agp-matrix__total">
                          {isText ? <span className="bd-muted">—</span> : <span className={differs ? "bd-agp-rollup--differs" : ""}>{formatTarget(rollup, d.format)}</span>}
                          {differs && canEditTeam && (
                            <button type="button" className="bd-btn bd-btn--ghost bd-agp-use-rollup" title="Set the team target to the sum of member targets" onClick={() => applyRollup(d)}>
                              use
                            </button>
                          )}
                        </td>
                        <td className="bd-agp-matrix__target">
                          <EditableCell
                            value={isText ? d.targetText : d.target}
                            format={d.format}
                            canEdit={canEditTeam}
                            onSave={(v) => saveTeamDriver(d.key, isText ? { targetText: v } : { target: v })}
                          />
                        </td>
                        <td className="bd-agp-matrix__actual">
                          {isText ? (
                            <span className="bd-muted">—</span>
                          ) : (
                            <div className="bd-agp-cell">
                              <div className="bd-agp-cell__values">
                                <strong>{d.actual === null || d.actual === undefined ? "—" : formatTarget(d.actual, d.format)}</strong>
                                {d.expectedYtd !== null && d.expectedYtd !== undefined && !d.snapshot && (
                                  <small className="bd-muted"> exp. {formatTarget(d.expectedYtd, d.format)}</small>
                                )}
                              </div>
                              <div className="bd-agp-cell__meta">
                                <PaceChip row={d} compact />
                                {d.key === "grossRevenue" && teamGoals.closedWon && teamGoals.closedWon.amount > 0 && (
                                  <small className="bd-muted" title="Salesforce closed-won (owner-attributed) for reference">
                                    SF closed-won {formatTarget(teamGoals.closedWon.amount, "currency")}
                                  </small>
                                )}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </MatrixScroller>
          </Card>
        );
      })}

      <MeetingRatesCard members={members} window={yearWindow} search={agpYear.search} />

      {canEditTeam && (
        <Card title="Members" subtitle="People with a goals row in this practice's sheet">
          <div className="bd-agp-members">
            {members.map((m) => (
              <span key={m.sfUserId} className="bd-agp-members__chip">
                <Avatar name={m.member.name} team={m.member.team} size="sm" src={avatarSrc(m.member)} />
                {m.member.name}
                {m.shared && <small className="bd-muted">shared</small>}
                {!m.virtual && (
                  <button
                    type="button"
                    className="bd-agp-members__remove"
                    title="Remove this person's row (their targets for this practice will be deleted)"
                    onClick={() => {
                      if (window.confirm(`Remove ${m.member.name}'s ${team.practice} goals row for ${year}?`)) mutations.removeMember(team.key, year, m.sfUserId);
                    }}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
            <span className="bd-agp-members__add">
              <select className="bd-select" value={addingId} onChange={(e) => setAddingId(e.target.value)}>
                <option value="">Add a person…</option>
                {addableMembers.map((u) => (
                  <option key={u.sfUserId} value={u.sfUserId}>
                    {u.name} ({u.team})
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="bd-btn bd-btn--secondary"
                disabled={!addingId}
                onClick={async () => {
                  await mutations.addMember(team.key, year, addingId);
                  setAddingId("");
                }}
              >
                Add
              </button>
            </span>
          </div>
        </Card>
      )}

      <ActualsPanel
        title="Record actuals"
        subtitle="Manually tracked drivers (revenue, sales leads, MQLs, …) for the practice"
        drivers={teamGoals.drivers}
        entries={teamGoals.manualActuals}
        canEdit={canEditTeam}
        onAdd={(body) => mutations.addActual({ year, scopeType: "team", teamKey: team.key, ...body })}
        onDelete={(id) => mutations.deleteActual(id)}
      />

      {teamGoals.history && teamGoals.history.length > 0 && (
        <Card
          title="History"
          subtitle={`Last updated ${teamGoals.updatedAt ? formatDateTime(teamGoals.updatedAt) : "—"}${teamGoals.updatedBy ? ` by ${teamGoals.updatedBy}` : ""}`}
          actions={
            <button type="button" className="bd-btn bd-btn--ghost" onClick={() => setShowHistory((s) => !s)}>
              {showHistory ? "Hide" : "Show"} changes
            </button>
          }
        >
          {showHistory && (
            <ul className="bd-agp-history">
              {teamGoals.history.map((h, i) => (
                <li key={i}>
                  <span className="bd-muted">{formatDateTime(h.at)}</span> <strong>{h.employeeName || h.employeeEmail}</strong> — {h.summary}
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
}
